import React, {
  FC,
  memo,
  PropsWithChildren,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { Alert, Spin } from 'antd';
import { HttpService } from '@core/services';
import { PaceTaskHttpService } from '@core/services/http';
import { PaceTaskTabScreens } from '@models/enums';
import {
  PaceTaskScreen,
  PaceTaskScreenResponse,
  TabComponentProps,
} from '@models/interfaces';
import { PaceTasks } from '@shared/components';
import { PrimaryButton } from '@shared/modules';
import { AddExistsPaceTaskModal, ArchiveModal } from '../../modals';
import { useScreenPaceTasks } from './hooks/use-screen-pace-tasks';
import { paceTaskColumns } from './models/constants';

import './styles.scss';

const PagePaceTaskComponent: FC<TabComponentProps> = ({
  isActive,
}: PropsWithChildren<TabComponentProps>) => {
  const paceTaskService = useMemo(
    () => HttpService.getHttpRequests(PaceTaskHttpService),
    [],
  );

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const screen = searchParams.get('tabName') as PaceTaskTabScreens;

  const {
    data: screenData,
    isLoading,
    hasError,
    invalidate,
  } = useScreenPaceTasks(screen, isActive);

  const [archiveModalVisible, setArchiveModalVisible] =
    useState<boolean>(false);
  const [addPaceTaskModalVisible, setAddPaceTaskModalVisible] =
    useState<boolean>(false);

  const archivePaceTask = useCallback(
    (paceTaskScreen: PaceTaskScreen): Promise<PaceTaskScreenResponse> =>
      paceTaskService.movePaceTaskToArchive(screen, paceTaskScreen),
    [screen],
  );

  const columns = useMemo(
    () => paceTaskColumns({ archivePaceTask }),
    [archivePaceTask],
  );

  const archivedColumns = useMemo(
    () => paceTaskColumns({ isArchived: true }),
    [],
  );

  const toggleAddPaceTaskModal = useCallback(
    (): void => setAddPaceTaskModalVisible((v: boolean): boolean => !v),
    [],
  );

  const toggleArchiveModal = useCallback(
    (): void => setArchiveModalVisible((v: boolean): boolean => !v),
    [],
  );

  const moveRow = useCallback(
    (fromIndex: number, toIndex: number): void => {
      const row: PaceTaskScreen = screenData[fromIndex];

      paceTaskService.updatePaceTaskScreen(screen, {
        ...row,
        order: toIndex + 1,
      });
    },
    [screen, screenData],
  );

  if (hasError) {
    return (
      <div className="prov-pace-task-screen prov-pace-task-screen--error">
        <Alert type="error" message="Failed to load screen tasks" />
        <PrimaryButton title="Retry" onClick={invalidate} />
      </div>
    );
  }

  return (
    <Spin spinning={isLoading} size="large">
      <ArchiveModal
        columns={archivedColumns}
        screen={screen}
        onOk={toggleArchiveModal}
        onCancel={toggleArchiveModal}
        visible={archiveModalVisible}
      />
      <AddExistsPaceTaskModal
        screen={screen}
        visible={addPaceTaskModalVisible}
        onOk={toggleAddPaceTaskModal}
        onCancel={toggleAddPaceTaskModal}
      />
      <PaceTasks
        className="prov-pace-task-screen"
        columns={columns}
        data={screenData}
        moveRow={moveRow}
        infoRowLeftElement={
          <PrimaryButton
            title="Add Task"
            icon="+"
            onClick={toggleAddPaceTaskModal}
          />
        }
        infoRowRightElement={
          <PrimaryButton title="Archive" onClick={toggleArchiveModal} />
        }
      />
    </Spin>
  );
};

export const PagePaceTask = memo(PagePaceTaskComponent);
